import React, { useMemo } from 'react'
import { useRouter } from 'next/router'
import dayjs from 'utils/dayjs'
import DropDownGroup from 'components/modules/DropDownGroup/DropDownGroup'
import { fireAnalyticsEvent } from '~analyticsUtils'
import { DATE_FORMAT } from '~constants'
import { DropDownGroupProps } from '~types'
import { DropDownFilterProps } from '../../../../types/index'
import { useWindowSize } from '~utils/hooks'
import styles from './DropDownMonthFilter.module.scss'
import { useEventsStore } from 'store/useEventsStore'

type MonthOption = { label: string; shortLabel: string; value: string }

const DropDownMonthFilter = ({
  selectedMonth,
  handleMonthNavChange,
  activeMonthList,
  isLoading
}: DropDownFilterProps) => {
  const { setIsNavigationClicked } = useEventsStore()
  const { asPath } = useRouter()
  const linkModule = 'calendar_month_filter'
  const { isBreakpoint: isBelowBreakpointSm } = useWindowSize('xs', '<')
  const labelField = isBelowBreakpointSm ? 'shortLabel' : 'label'
  const monthOptions: MonthOption[] = useMemo(() => {
    return (activeMonthList || []).map(m => {
      const mObj = dayjs(m, DATE_FORMAT.MONTH_YEAR_SHORT)
      return {
        label: mObj.format(DATE_FORMAT.MONTH_YEAR_LABEL),
        shortLabel: mObj.format(`${DATE_FORMAT.SHORT_MONTH} ${DATE_FORMAT.SHORT_YEAR}`),
        value: mObj.format(DATE_FORMAT.MONTH_YEAR_VALUE)
      }
    })
  }, [activeMonthList])

  const options: NonNullable<DropDownGroupProps['dropDowns']>[number]['options'] = monthOptions.map(m => ({
    label: m[labelField],
    value: m.value
  }))

  const selectedValue = selectedMonth?.value ?? options?.[0]?.value

  const onChange: DropDownGroupProps['onDropDownChange'] = (value?: string) => {
    const next = monthOptions.find(m => m.value === value) || monthOptions[0]
    if (!next) return
    setIsNavigationClicked(true)
    const wasActive = selectedMonth?.value === next.value

    handleMonthNavChange(next)

    if (!wasActive) {
      fireAnalyticsEvent(null, asPath, {
        linkModule,
        clickTracking: `${linkModule}:${next.value}`
      })
    }
  }

  const dropDowns: DropDownGroupProps['dropDowns'] = [
    {
      id: 'month-filter',
      dropDownOption: selectedValue,
      options
    }
  ]

  return (
    <div className={styles.dropdownMonthFilter}>
      <DropDownGroup
        dropDowns={dropDowns}
        onDropDownChange={onChange}
        theme='light'
        showPlaceholderInMenu={false}
        isLoading={isLoading}
      />
    </div>
  )
}

export default DropDownMonthFilter
